"use client";

import { c as _c } from "react/compiler-runtime";
import { FaCheck } from "react-icons/fa6";
import { cx } from "@/lib/tailwind-utils";
export type VerticalStepperStatus = "complete" | "current" | "upcoming" | "skipped"
/** Waiting on a third party. Nothing for the user to do. */ | "attention"
/** Someone is waiting on the user. */ | "action";
export interface VerticalStepperStep {
  label: string;
  status: VerticalStepperStatus;
  /** Inline annotation beside the label, e.g. "Awaiting approval". */
  note?: string;
  /** Whether clicking this step should call onStepClick. */
  clickable?: boolean;
  /** Falls back to the index, which mis-associates rows if the caller reorders. */
  id?: string;
}
interface VerticalStepperProps {
  steps: VerticalStepperStep[];
  onStepClick?: (index: number) => void;
  /** Class names, so the stepper works on any surface — including inverted ones. */
  accentClassName?: string;
  accentContrastClassName?: string;
  /** Markers only, no labels. Each keeps its label as an accessible name and title. */
  compact?: boolean;
  /** Stagger the rows in on mount. Off for compact rails, which are chrome. */
  animateOnMount?: boolean;
  className?: string;
}
const isSettled = (status: VerticalStepperStatus) => status !== "current" && status !== "upcoming";

/** Vertical step rail. Colors derive from the inherited text color plus the accent. */
export function VerticalStepper(t0) {
  const $ = _c(13);
  const {
    steps,
    onStepClick,
    accentClassName: t1,
    accentContrastClassName: t2,
    compact: t3,
    animateOnMount: t4,
    className
  } = t0;
  const accentClassName = t1 === undefined ? "text-primary" : t1;
  const accentContrastClassName = t2 === undefined ? "text-background" : t2;
  const compact = t3 === undefined ? false : t3;
  const animateOnMount = t4 === undefined ? true : t4;
  const t5 = compact && "w-7";
  let t6;
  if ($[0] !== className || $[1] !== t5) {
    t6 = cx("relative", t5, className);
    $[0] = className;
    $[1] = t5;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  let t7;
  if ($[3] !== accentClassName || $[4] !== accentContrastClassName || $[5] !== animateOnMount || $[6] !== compact || $[7] !== onStepClick || $[8] !== steps) {
    t7 = steps.map((step, index) => {
      const isFirst = index === 0;
      const isLast = index === steps.length - 1;
      const isClickable = Boolean(step.clickable && onStepClick);
      const reachedHere = isSettled(step.status);
      const reachedPrevious = index > 0 && isSettled(steps[index - 1].status);
      const statusLabel = step.status === "skipped" ? "skipped" : step.status === "attention" || step.status === "action" ? step.note ?? "needs attention" : step.status === "complete" ? "complete" : step.status === "current" ? "current" : "upcoming";
      const aria = `${step.label}, ${statusLabel}`;
      const marker = <StepMarker index={index} status={step.status} accentClassName={accentClassName} accentContrastClassName={accentContrastClassName} />;
      return <li key={step.id ?? index} className={cx("flex", !compact && "gap-4", animateOnMount && "animate-numberSlideIn motion-reduce:animate-none")} style={animateOnMount ? {
        animationDelay: `${index * 40}ms`
      } : undefined}><div className="flex shrink-0 flex-col items-center gap-1 self-stretch"><Segment visible={!isFirst} filled={reachedPrevious} isLowerHalf={true} accentClassName={accentClassName} /><div className="flex">{!compact ? marker : isClickable ? <button type="button" onClick={() => onStepClick?.(index)} aria-label={aria} title={aria} className="cursor-pointer">{marker}</button> : <span title={aria}>{marker}<span className="sr-only">{aria}</span></span>}</div><Segment visible={!isLast} filled={reachedHere} isLowerHalf={false} accentClassName={accentClassName} /></div>{!compact && <StepBody step={step} statusLabel={statusLabel} isClickable={isClickable} accentClassName={accentClassName} onClick={() => onStepClick?.(index)} />}</li>;
    });
    $[3] = accentClassName;
    $[4] = accentContrastClassName;
    $[5] = animateOnMount;
    $[6] = compact;
    $[7] = onStepClick;
    $[8] = steps;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t6 || $[11] !== t7) {
    t8 = <ol className={t6}>{t7}</ol>;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
}

/**
 * Half of the line between two markers, sequenced so the pair reads as one:
 * filling, the upper half goes first and the line draws downward; emptying, the
 * lower half goes first and it retracts back the way it came.
 */
function Segment(t0) {
  const $ = _c(8);
  const {
    visible,
    filled,
    isLowerHalf,
    accentClassName
  } = t0;
  if (!visible) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className="min-h-2.5 flex-1" aria-hidden={true} />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="absolute inset-0 bg-current opacity-15" />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = filled ? "scale-y-100" : "scale-y-0";
  const t3 = filled === isLowerHalf && "delay-300";
  let t4;
  if ($[2] !== accentClassName || $[3] !== t2 || $[4] !== t3) {
    t4 = cx("absolute inset-0 origin-top bg-current transition-transform duration-300 ease-linear motion-reduce:transition-none", accentClassName, t2, t3);
    $[2] = accentClassName;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <span aria-hidden={true} className="relative min-h-2.5 w-0.5 flex-1">{t1}<span className={t4} /></span>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
function StepMarker(t0) {
  const $ = _c(19);
  const {
    index,
    status,
    accentClassName,
    accentContrastClassName
  } = t0;
  const isComplete = status === "complete";
  const isCurrent = status === "current";
  const isUnfinished = status === "attention" || status === "action";
  const t1 = (isComplete || isCurrent) && accentClassName;
  const t2 = isComplete && "bg-current";
  const t3 = isUnfinished && "border border-current opacity-60";
  const t4 = isCurrent && "border-2 border-current font-medium delay-700";
  const t5 = status === "skipped" && "border border-dashed border-current opacity-45";
  const t6 = status === "upcoming" && "border border-current opacity-40";
  let t7;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3 || $[3] !== t4 || $[4] !== t5 || $[5] !== t6) {
    t7 = cx("relative z-10 flex h-7 w-7 shrink-0 select-none items-center justify-center rounded-full text-caption transition-all duration-300 motion-reduce:transition-none", t1, t2, t3, t4, t5, t6);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const t8 = isCurrent ? "opacity-10 delay-700" : "opacity-0";
  let t9;
  if ($[7] !== t8) {
    t9 = cx("absolute inset-0 -z-10 rounded-full bg-current transition-opacity duration-300 motion-reduce:transition-none", t8);
    $[7] = t8;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== t9) {
    t10 = <span aria-hidden={true} className={t9} />;
    $[9] = t9;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== accentContrastClassName || $[12] !== index || $[13] !== isComplete) {
    t11 = isComplete ? <FaCheck className={cx("h-3 w-3", accentContrastClassName)} aria-hidden={true} /> : <span className="tabular-nums">{index + 1}</span>;
    $[11] = accentContrastClassName;
    $[12] = index;
    $[13] = isComplete;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t10 || $[16] !== t11 || $[17] !== t7) {
    t12 = <span className={t7}>{t10}{t11}</span>;
    $[15] = t10;
    $[16] = t11;
    $[17] = t7;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  return t12;
}
function StepBody(t0) {
  const $ = _c(22);
  const {
    step,
    statusLabel,
    isClickable,
    accentClassName,
    onClick
  } = t0;
  let t1;
  if ($[0] !== statusLabel) {
    t1 = <span className="sr-only">{statusLabel}</span>;
    $[0] = statusLabel;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = step.status === "current" ? "text-body-emphasis" : step.status === "upcoming" ? "text-body opacity-60" : "text-body-strong opacity-75";
  let t3;
  if ($[2] !== t2) {
    t3 = cx("transition-opacity duration-300 motion-reduce:transition-none", t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== step.label || $[5] !== t3) {
    t4 = <span className={t3}>{step.label}</span>;
    $[4] = step.label;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== accentClassName || $[8] !== step.note || $[9] !== step.status) {
    t5 = step.note && <span className={cx("text-caption", step.status === "attention" ? accentClassName : "opacity-60")}>{step.note}</span>;
    $[7] = accentClassName;
    $[8] = step.note;
    $[9] = step.status;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <span className="flex flex-wrap items-baseline gap-x-2">{t4}{t5}</span>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t1 || $[15] !== t6) {
    t7 = <>{t1}{t6}</>;
    $[14] = t1;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const content = t7;
  if (!isClickable) {
    let t8;
    if ($[17] !== content) {
      t8 = <span className="flex min-w-0 items-center py-2.5">{content}</span>;
      $[17] = content;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    return t8;
  }
  let t8;
  if ($[19] !== content || $[20] !== onClick) {
    t8 = <button type="button" onClick={onClick} className="flex min-w-0 cursor-pointer items-center py-2.5 text-left transition-opacity hover:opacity-90 motion-reduce:transition-none">{content}</button>;
    $[19] = content;
    $[20] = onClick;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
}